/**
 * WaitlistManagement - Manage event waitlist entries
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Mail, Trash2, Loader2, Send, CheckCircle, XCircle, Users } from 'lucide-react';
import { waitlistAPI, getErrorMessage } from '@/lib/api';
import api from '@/lib/api';

const STATUS_COLORS = { waiting: 'bg-yellow-100 text-yellow-800', offered: 'bg-blue-100 text-blue-800', accepted: 'bg-green-100 text-green-800', declined: 'bg-red-100 text-red-800', expired: 'bg-gray-100 text-gray-800' };

const WaitlistManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [filterTier, setFilterTier] = useState('all');
  const [offerEntry, setOfferEntry] = useState(null);
  const [offerHours, setOfferHours] = useState(48);

  const { data: waitlist = [], isLoading } = useQuery({
    queryKey: ['waitlist', eventId, filterTier],
    queryFn: () => waitlistAPI.list(eventId, filterTier === 'all' ? undefined : filterTier).then(r => r.data),
  });

  const { data: tiers = [] } = useQuery({
    queryKey: ['event-tiers', eventId],
    queryFn: () => api.get(`/events/${eventId}/tiers`).then(r => r.data),
  });

  const offerMutation = useMutation({
    mutationFn: ({ entryId, hours }) => waitlistAPI.offer(eventId, entryId, hours),
    onSuccess: () => { toast.success('Ticket offer sent!'); queryClient.invalidateQueries(['waitlist', eventId]); setOfferEntry(null); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => waitlistAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Entry updated'); queryClient.invalidateQueries(['waitlist', eventId]); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const removeMutation = useMutation({
    mutationFn: (id) => waitlistAPI.remove(eventId, id),
    onSuccess: () => { toast.success('Removed from waitlist'); queryClient.invalidateQueries(['waitlist', eventId]); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const getTierName = (tierId) => tiers.find(t => t.id === tierId)?.name || tierId;
  const formatDate = (dt) => dt ? new Date(dt).toLocaleString() : '';

  const stats = {
    total: waitlist.length,
    waiting: waitlist.filter(e => e.status === 'waiting').length,
    offered: waitlist.filter(e => e.status === 'offered').length,
    accepted: waitlist.filter(e => e.status === 'accepted').length,
  };

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="waitlist-management">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" />Waitlist Management</h2>
        <Select value={filterTier} onValueChange={setFilterTier}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Tiers" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Tiers</SelectItem>{tiers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-slate-500">Total</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-yellow-600">{stats.waiting}</p><p className="text-sm text-slate-500">Waiting</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-blue-600">{stats.offered}</p><p className="text-sm text-slate-500">Offered</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-green-600">{stats.accepted}</p><p className="text-sm text-slate-500">Accepted</p></CardContent></Card>
      </div>

      {/* Waitlist Table */}
      <Card>
        <CardHeader><CardTitle>Waitlist Entries</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {waitlist.map((entry, idx) => (
              <div key={entry.id} data-testid={`waitlist-${entry.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className="text-lg font-bold text-slate-400">#{entry.position || idx + 1}</span>
                  <div>
                    <p className="font-medium">{entry.first_name} {entry.last_name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-2">
                      <Mail className="h-3 w-3" />{entry.email}
                      <span>•</span>
                      <Badge variant="outline">{getTierName(entry.tier_id)}</Badge>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={STATUS_COLORS[entry.status]}>{entry.status}</Badge>
                  {entry.status === 'offered' && entry.offer_expires_at && (
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />Expires: {formatDate(entry.offer_expires_at)}</span>
                  )}
                  <div className="flex gap-1">
                    {entry.status === 'waiting' && (
                      <Button size="sm" variant="outline" onClick={() => setOfferEntry(entry)}><Send className="h-4 w-4 mr-1" />Offer</Button>
                    )}
                    {entry.status === 'offered' && (
                      <>
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateMutation.mutate({ id: entry.id, data: { status: 'accepted' }})}><CheckCircle className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => updateMutation.mutate({ id: entry.id, data: { status: 'declined' }})}><XCircle className="h-4 w-4" /></Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeMutation.mutate(entry.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              </div>
            ))}
            {waitlist.length === 0 && <p className="text-center text-slate-500 py-8">No one on the waitlist yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Offer Dialog */}
      <Dialog open={!!offerEntry} onOpenChange={() => setOfferEntry(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Send Ticket Offer</DialogTitle></DialogHeader>
          {offerEntry && (
            <div className="space-y-4">
              <p>Send ticket offer to <strong>{offerEntry.first_name} {offerEntry.last_name}</strong> ({offerEntry.email})?</p>
              <div><Label>Offer Expires In</Label>
                <Select value={offerHours.toString()} onValueChange={v => setOfferHours(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="48">48 hours</SelectItem>
                    <SelectItem value="72">72 hours</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOfferEntry(null)}>Cancel</Button>
                <Button onClick={() => offerMutation.mutate({ entryId: offerEntry.id, hours: offerHours })}><Send className="h-4 w-4 mr-2" />Send Offer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaitlistManagement;
